import { saveAs } from 'file-saver';
import { DownloadIcon, EyeIcon } from '@heroicons/react/outline';
import uniq from 'lodash/uniq';
import map from 'lodash/map';
import omit from 'lodash/omit';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import {
  projectMilestones,
  apiZedocOneProject,
  apiZedocGetCustomPDF,
  apiZedocGetCompletedMilestoneListWithCustomPDF,
  apiZedocSearchResponses,
} from '../../../common/api/zedoc';
import { default as ProjectMilestoneSelect } from '../../../common/selectors/ProjectMilestone';
import { default as ProjectSelect } from '../../../common/selectors/Project';
import { default as AnswersSheetSelect } from '../../../common/selectors/AnswersSheet';
import AnswersSheet from '../../../common/models/AnswersSheet';
import TranslationsSelect from '../../inputs/TranslationsSelect';
import useInFlight, { guardAsync } from '../../../utils/hooks/useInFlight';
import Button from '../../Button';
import CustomPDF from './CustomPDF';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { notifyError } from '../../../utils/notify';
import settings from '../../../common/settings';
import { useRum } from '../../../containers/RumContext';
import usePagination from '../../../utils/usePagination';
import useClientPagination from '../../../utils/useClientPagination';
import cleanFilters from '../../../utils/cleanFilters';
import {
  SORTER_TYPE__PROPERTY,
  SORTING_ORDER__DESCENDING,
} from '../../../common/constants';
import { getDocumentsFilters } from '../../../screens/PatientCard/store';
import ConnectedFilters from './ConnectedFilters';
import Cluster from '../../../common/components/primitives/Cluster';
import Tabs from '../../layouts/Tabs';
import Tab from '../../../common/components/Tabs/Tab';
import filterCustomPDFs from './filterCustomPDFs';
import DocumentRow, {
  formatCompletedAt,
  joinDocumentParts,
} from './DocumentRow';

const DOCUMENTS_TAB__DEFAULT = 'default';
const DOCUMENTS_TAB__CUSTOM = 'custom';

const {
  features: { enableCustomReports },
} = settings.public;

const ProjectMilestonesSubscription = ({ projectId }) => {
  useDDPSubscription(
    projectId ? projectMilestones.withParams({ projectId }) : null,
  );
  return null;
};

const ProjectSubscription = ({ projectId }) => {
  useDDPSubscription(
    projectId ? apiZedocOneProject.withParams({ projectId }) : null,
  );
  return null;
};

ProjectSubscription.propTypes = {
  projectId: PropTypes.string,
};

ProjectSubscription.defaultProps = {
  projectId: null,
};

ProjectMilestonesSubscription.propTypes = {
  projectId: PropTypes.string,
};

ProjectMilestonesSubscription.defaultProps = {
  projectId: null,
};

const DownloadCard = ({
  answersSheet,
  onPreview,
  onDownload,
  translationItems,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [answersSheetLanguage, setAnswersSheetLanguage] = useState();
  // Which audiences' PDFs are being generated right now, so each clicked
  // button reads "Downloading…" with its spinner. A Set, because the patient
  // and provider downloads may be in flight at the same time.
  const [downloading, setDownloading] = useState(() => new Set());
  const isDownloading = (audience) => downloading.has(audience);

  const handleOnPreview = onPreview ? () => onPreview(answersSheet) : null;
  const handleOnDownload = (audience) => {
    setDownloading((prev) => new Set(prev).add(audience));
    const clear = () =>
      setDownloading((prev) => {
        if (!prev.has(audience)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(audience);
        return next;
      });
    return guardAsync(
      () =>
        onDownload({
          references: { answersSheetId: answersSheet._id },
          audience,
          language: answersSheetLanguage,
        }),
      clear,
    );
  };

  const milestone = useSelector(
    ProjectMilestoneSelect.one().whereIdEquals(answersSheet.milestoneId),
  );
  const project = useSelector(
    ProjectSelect.one().whereIdEquals(answersSheet.projectId),
  );

  const projectName = project?.getName?.() || project?.name;
  const answersSheetLanguages = answersSheet.language
    ? [answersSheet.language]
    : [];
  // `translationItems` is defined (an array, possibly empty) only when the
  // publication attached it; undefined means this consumer did not.
  const hasServerTranslationOptions = translationItems != null;

  return (
    <DocumentRow
      primaryLine={joinDocumentParts([
        milestone?.name,
        answersSheet.questionnaireId,
      ])}
      secondaryLine={formatCompletedAt({
        t,
        language,
        completedAt: answersSheet.completedAt,
      })}
      tertiaryLine={projectName}
      actions={
        <>
          {handleOnPreview && (
            <Button type="ghost" icon={<EyeIcon />} onClick={handleOnPreview}>
              {t('preview')}
            </Button>
          )}
          {onDownload && (
            <>
              <TranslationsSelect
                type="primary"
                answersSheetId={answersSheet._id}
                answersSheetLanguages={answersSheetLanguages}
                onChange={setAnswersSheetLanguage}
                value={answersSheetLanguage}
                valueAsLanguage
                // When the publication attached translationOptions
                // (searchResponses path) use them and skip the per-row
                // subscription. When absent (Projects Dashboard documents view,
                // which feeds answersSheets from a publication that does not
                // attach them) fall back to the per-sheet subscription so the
                // language picker still renders.
                items={hasServerTranslationOptions ? translationItems : null}
                enabled={!hasServerTranslationOptions}
              />
              <Button
                icon={<DownloadIcon />}
                loading={isDownloading('patient')}
                onClick={() => handleOnDownload('patient')}
              >
                {isDownloading('patient') ? t('downloading') : t('patientPDF')}
              </Button>
              <Button
                icon={<DownloadIcon />}
                loading={isDownloading('provider')}
                onClick={() => handleOnDownload('provider')}
              >
                {isDownloading('provider')
                  ? t('downloading')
                  : t('providerPDF')}
              </Button>
            </>
          )}
        </>
      }
    />
  );
};

DownloadCard.propTypes = {
  answersSheet: PropTypes.instanceOf(AnswersSheet),
  onPreview: PropTypes.func,
  onDownload: PropTypes.func.isRequired,
  translationItems: PropTypes.arrayOf(PropTypes.object),
};

DownloadCard.defaultProps = {
  answersSheet: {},
  onPreview: null,
  // null (not []) so the dashboard path — which passes no translationOptions —
  // triggers the per-sheet subscription fallback rather than an empty list.
  translationItems: null,
};

const PatientDocuments = ({
  recipientId,
  answersSheets: answersSheetsProp,
  onPreview,
}) => {
  const {
    t,
    i18n: { language: uiLanguage },
  } = useTranslation();
  const awsRum = useRum();
  const dispatch = useDispatch();
  const [customPDFs, setCustomPDFs] = useState([]);
  const [documentsTab, setDocumentsTab] = useState(DOCUMENTS_TAB__DEFAULT);

  const rawFilters = useSelector(getDocumentsFilters);
  const filters = useMemo(
    () => cleanFilters(rawFilters, { anyOf: true }),
    [rawFilters],
  );
  const filteredCustomPDFs = useMemo(
    () => filterCustomPDFs(customPDFs, rawFilters),
    [customPDFs, rawFilters],
  );
  const {
    items: paginatedCustomPDFs,
    renderPagination: renderCustomPagination,
  } = useClientPagination(filteredCustomPDFs);
  const sorter = useMemo(
    () => ({
      type: SORTER_TYPE__PROPERTY,
      settings: {
        id: 'completedAt',
      },
      order: SORTING_ORDER__DESCENDING,
    }),
    [],
  );

  const { items: paginatedAnswersSheets, renderPagination } = usePagination({
    // Back to the first page whenever the filters change, see usePagination.
    resetPageKey: map(filters, (filter) => omit(filter, 'meta')),
    debounceMs: 500,
    selector: AnswersSheetSelect,
    getSubscription: (currentPage, resultsPerPage) =>
      !answersSheetsProp &&
      recipientId &&
      apiZedocSearchResponses.withParams({
        recipientId,
        filters: map(filters, (filter) => omit(filter, 'meta')),
        sorter,
        controlId: '$meta.id',
        pageIndex: currentPage - 1,
        resultsPerPage,
        uiLanguage,
      }),
  });

  const answersSheets = answersSheetsProp || paginatedAnswersSheets;

  const projectIds = useMemo(
    () =>
      uniq(
        [
          ...map(answersSheets, 'projectId'),
          ...map(customPDFs, 'projectId'),
        ].filter(Boolean),
      ),
    [answersSheets, customPDFs],
  );

  // One PDF generation per (document, audience, language, template) at a
  // time. A repeat click while one is in flight returns the pending promise
  // instead of issuing a second server-side render (TECH-3695 QA-3 Defect 1).
  // The promise is returned so the clicked Button shows its spinner and stays
  // disabled until the download settles.
  const { run: runDownload } = useInFlight();

  const onDownload = useCallback(
    ({
      references,
      audience,
      language,
      templateId,
      reportType = 'default',
    }) => {
      const inFlightKey = JSON.stringify([
        references,
        audience,
        language,
        templateId,
        reportType,
      ]);

      return runDownload(inFlightKey, () => {
        const startTime = new Date().getTime();
        return dispatch(
          callMethod(apiZedocGetCustomPDF, {
            references,
            audience,
            language,
            reportType,
            templateId,
          }),
        )
          .then((url) => {
            awsRum?.recordEvent('com.zedoc.rum.get_pdf_for_answers_sheet', {
              duration: new Date().getTime() - startTime,
            });

            const blob = new Blob([url], {
              type: 'application/pdf',
            });

            const referenceValue = references
              ? Object.values(references)[0]
              : '';
            const referenceStr = referenceValue ? `-${referenceValue}` : '';
            const reportTypeStr =
              reportType === 'default' ? 'default' : 'custom';

            saveAs(blob, `response-${reportTypeStr}${referenceStr}.pdf`);
          })
          .catch(notifyError());
      });
    },
    [dispatch, awsRum, runDownload],
  );

  useEffect(() => {
    if (!recipientId) return;

    if (enableCustomReports) {
      dispatch(
        callMethod(apiZedocGetCompletedMilestoneListWithCustomPDF, {
          recipientId,
        }),
      )
        .then((data) => setCustomPDFs(data || []))
        .catch(notifyError);
    }
  }, [recipientId, dispatch]);

  useEffect(() => {
    setDocumentsTab(DOCUMENTS_TAB__DEFAULT);
  }, [recipientId]);

  const documentTabs = useMemo(() => {
    const tabs = [
      {
        label: t('documentsTabDefault'),
        value: DOCUMENTS_TAB__DEFAULT,
        content: (
          <>
            <div className="border border-divider rounded-md divide-y divide-divider px-4">
              {answersSheets.map((answersSheet) => (
                <DownloadCard
                  key={answersSheet._id}
                  answersSheet={answersSheet}
                  onPreview={onPreview}
                  onDownload={onDownload}
                  translationItems={answersSheet.translationOptions}
                />
              ))}
            </div>
            {!answersSheetsProp && (
              <Cluster justify="flex-end">{renderPagination()}</Cluster>
            )}
          </>
        ),
      },
    ];

    if (enableCustomReports) {
      tabs.push({
        label: t('documentsTabCustom'),
        value: DOCUMENTS_TAB__CUSTOM,
        content: (
          <>
            <div className="border border-divider rounded-md divide-y divide-divider px-4">
              {paginatedCustomPDFs.map((pdf) => (
                <CustomPDF
                  key={`${pdf.activityId}-${pdf.pdfReportId}`}
                  title={pdf.pdfReportName}
                  projectName={pdf.projectName}
                  milestoneName={pdf.milestoneName}
                  completedAt={pdf.milestoneCompletedAt}
                  answersSheets={pdf.answersSheets}
                  onDownload={() =>
                    onDownload({
                      references: { activityId: pdf.activityId },
                      templateId: pdf.pdfReportId,
                      reportType: pdf.pdfReportType,
                    })
                  }
                />
              ))}
            </div>
            <Cluster justify="flex-end">{renderCustomPagination()}</Cluster>
          </>
        ),
      });
    }

    return tabs;
  }, [
    t,
    answersSheets,
    answersSheetsProp,
    onPreview,
    onDownload,
    renderPagination,
    paginatedCustomPDFs,
    renderCustomPagination,
  ]);

  const documentsTabExtra = useMemo(() => {
    if (!enableCustomReports || answersSheetsProp) {
      return null;
    }

    return (
      <ul
        className="flex list-none overflow-x-auto overflow-y-hidden"
        role="tablist"
      >
        {documentTabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            activeKey={documentsTab}
            onChange={setDocumentsTab}
          />
        ))}
      </ul>
    );
  }, [documentTabs, documentsTab, answersSheetsProp]);

  const activeTabContent = useMemo(
    () => documentTabs.find((tab) => tab.value === documentsTab)?.content,
    [documentTabs, documentsTab],
  );

  return (
    <div className="stack-6">
      {projectIds.map((projectId) => (
        <React.Fragment key={projectId}>
          <ProjectSubscription projectId={projectId} />
          <ProjectMilestonesSubscription projectId={projectId} />
        </React.Fragment>
      ))}
      <div className="stack-4">
        {!answersSheetsProp ? (
          <>
            <div className="px-4">
              <ConnectedFilters
                recipientId={recipientId}
                extra={documentsTabExtra}
              />
            </div>
            {activeTabContent}
          </>
        ) : (
          <Tabs
            activeKey={documentsTab}
            onChange={setDocumentsTab}
            tabs={documentTabs}
            className="px-4"
          />
        )}
      </div>
    </div>
  );
};

PatientDocuments.propTypes = {
  recipientId: PropTypes.string,
  answersSheets: PropTypes.arrayOf(PropTypes.instanceOf(AnswersSheet)),
  onPreview: PropTypes.func,
};

PatientDocuments.defaultProps = {
  recipientId: null,
  answersSheets: null,
  onPreview: null,
};

export default PatientDocuments;
